<script setup>
  import {computed, inject, nextTick, onMounted, ref, watch} from "vue";

  import ComponentPrivacyGuard from "@components/Alerts/PrivacyGuard.vue";
  import ComponentAutocomplete from "@components/Autocomplete/Autocomplete.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentPatientReleaseConfidentiality from "@components/Dialogs/PatientReleaseConfidentiality/PatientReleaseConfidentiality.vue";
  import ComponentEditor from "@components/Editor/Editor.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import {router, useForm, usePage} from "@inertiajs/vue3";
  import {MedButton, MedCheckbox, MedFormField, MedInfoButton, MedLabel, MedLink} from "@pharma4u/medicheck-components";
  import {usePrivacyGuard} from "@utils/Helpers/PrivacyGuard.js";
  import readDom from "@utils/Helpers/ReadDom.js";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";

  defineOptions({
    name: "ComponentRecordSendMailDialog",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    record: {
      type: Object,
      required: true,
    },
  });

  const pageProperties = usePage().props;
  const {validatePrivacyObject} = usePrivacyGuard();

  const axios = inject("$axios"); // kept for preview() blob request
  const privacy = inject("$privacy");

  const broadcast = inject("$broadcast");
  const pendingAjaxRequests = inject("$pendingAjaxRequests");

  const simulateResultConfig = inject("simulateResultConfig");
  const restoreResultConfig = inject("restoreResultConfig");

  const isLoading = ref(false);
  const refDialog = ref(null);
  const showMailForm = ref(false);
  const type = ref(null);

  const recommendedRecipients = ref([]);
  const recommendedPatientEmailAddress = ref(null);

  const form = useForm({
    recipient_mail: null,
    sender_mail: pageProperties.client?.email ?? null,
    carbon_copy: true,
    password: null,
    password_confirmation: null,

    subject: "Arzneimittel-Konsil",
    body: null,
    html: null,
  });

  const recipientName = computed(() => {
    let temporary;

    if (type.value === "patient") {
      if (props.patient.gender === "male") {
        temporary = "Sehr geehrter Herr ";
      } else if (props.patient.gender === "female") {
        temporary = "Sehr geehrte Frau ";
      } else {
        temporary = "Sehr geehrte/r Herr/Frau ";
      }

      return temporary + (props.patient.lastname ?? "");
    }

    return "Sehr geehrte(r) " + (props.record.doctor ?? "");
  });

  const patientName = computed(() => {
    if (props.patient.gender === "diverse") {
      return "Ihre in Behandlung befindende Person";
    } else if (props.patient.gender === "male") {
      return "Ihr Patient";
    }

    return "Ihre Patientin";
  });

  const senderName = computed(() => {
    return privacy.getCurrentUser()?.name ?? pageProperties.client.first_name + " " + pageProperties.client.last_name;
  });

  const senderCompany = computed(() => {
    return pageProperties.client?.company ?? "";
  });

  const senderCity = computed(() => {
    return ((pageProperties.client?.zip ?? "") + " " + (pageProperties.client?.city ?? "")).trim();
  });

  watch(type, (newValue) => {
    switch (newValue) {
      case "doc":
        simulateResultConfig("showCommunicationDoc", true);
        simulateResultConfig("showCommunicationPatient", false);
        break;
      case "patient":
        simulateResultConfig("showCommunicationDoc", false);
        simulateResultConfig("showCommunicationPatient", true);
        break;
    }

    nextTick(() => {
      form.html = readDom(newValue);
    });
  });

  onMounted(() => {
    if (pageProperties.client.company !== null) {
      form.subject += " von " + pageProperties.client.company;
    } else if (pageProperties.client.firstname !== null || pageProperties.client.lastname !== null) {
      form.subject += " von " + pageProperties.client.firstname + " " + pageProperties.client.lastname; // user name
    }
  });

  function open() {
    refDialog.value.open();

    // init recommendedRecipients once
    useAuthHttp({patient_id: props.patient.id}).post(route("api.contacts.search"), {
      onSuccess: (response) => {
        recommendedRecipients.value = response?.data?.filter((contact) => {
          return contact.email !== null;
        });
      },
    });

    // check if patient email is present in patient_attributes
    const email = props.patient.patient_attributes.find((patientAttribute) => {
      return patientAttribute.type === "patientAddress";
    })?.payload?.email;

    if (email) {
      privacy.decryptValue(email).then((decryptedValue) => {
        recommendedPatientEmailAddress.value = decryptedValue;
      });
    }
  }

  function close() {
    form.reset();
    type.value = null;
    showMailForm.value = false;
    restoreResultConfig();
    refDialog.value.close();
  }

  function write() {
    showMailForm.value = true;
  }

  function handleMailBodyUpdate(event) {
    // the @input event is triggered from the trix editor itself
    // and still provides the full editor object
    form.body = typeof event === "string" ? event : event.target.innerHTML;
  }

  function send() {
    isLoading.value = true;

    form
      .transform((data) => ({
        ...data,
        type: type.value,
      }))
      .post(route("records.send-as-email", {patient: props.patient.id, record: props.record.id}), {
        preserveScroll: true,
        onSuccess: () => {
          // force reload to trigger update of easyCom icon
          router.reload();
          // reload list of records
          broadcast.postMessage({action: "reload.records"});
          // reload specific record
          broadcast.record.postMessage(props.record.id, {action: "reload.record"});

          close();
        },
        only: ["flash", "errors"],
        onFinish: () => {
          isLoading.value = false;
        },
      });
  }

  function preview() {
    isLoading.value = true;

    // immediately open new window (while user interaction is still active)
    const pdfWindow = window.open("", "_blank");

    if (!pdfWindow) {
      console.error("Popup wurde blockiert. Bitte Popup-Blocker deaktivieren.");
      isLoading.value = false;
      return;
    }

    pdfWindow.document.body.innerHTML = '<p style="font-family:sans-serif;">🕑 PDF-Vorschau wird geladen...</p>';

    axios
      .post(
        route("records.print", {patient: props.patient.id, record: props.record.id}),
        {
          type: type.value,
          html: form.html,
        },
        {
          responseType: "blob", // important
        },
      )
      .then((response) => {
        // handle pdf data as blob
        let pdfBlob = new Blob([response.data], {type: "application/pdf"});
        // create an object url representing the pdf file
        let pdfUrl = URL.createObjectURL(pdfBlob);

        // open new window with iframe containing the object url
        const iframe = pdfWindow.document.createElement("iframe");
        pdfWindow.document.body.style.margin = "0";
        iframe.width = "100%";
        iframe.height = "100%";
        iframe.src = pdfUrl;
        iframe.style.border = "none";
        pdfWindow.document.body.innerHTML = "";
        pdfWindow.document.body.append(iframe);
      })
      .catch((error) => {
        if (error.response) {
          console.error("Server responded with an error:", error.response.status, error.response.data);
        } else if (error.request) {
          console.error("No response received:", error.request);
        } else {
          console.error("Request setup error:", error.message);
        }
      })
      .finally(() => {
        isLoading.value = false;
      });
  }

  const privacyErrors = computed(() => {
    return type.value === "patient" ? {} : validatePrivacyObject(form, {subject: "Betreff", body: "Text"});
  });

  function autocompleteRecipient(searchTerm) {
    return new Promise((resolve) => {
      useAuthHttp({q: searchTerm}).post(route("api.contacts.search"), {
        onSuccess: (response) => {
          const searchResults = response?.data?.filter((contact) => {
            return contact.email !== null;
          });

          // inject an existing patient email address to contact search results
          if (
            recommendedPatientEmailAddress.value &&
            (props.patient?.firstname?.includes(searchTerm) ||
              props.patient?.lastname?.includes(searchTerm) ||
              recommendedPatientEmailAddress.value.includes(searchTerm))
          ) {
            resolve([
              {
                firstname: props.patient.firstname,
                lastname: props.patient.lastname,
                email: recommendedPatientEmailAddress.value,
              },
              ...searchResults,
            ]);
          } else {
            resolve(searchResults);
          }
        },
      });
    });
  }

  function selectRecipient(selectedContact) {
    form.recipient_mail = selectedContact.email;
  }

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog
    ref="refDialog"
    dialog-title="E-Mail senden (verschlüsselt)"
    :dialog-width="showMailForm ? 'large' : 'medium'"
    :is-loading="isLoading"
    :test-id="getTestId('send-mail')"
    @dialog-closed="restoreResultConfig()"
  >
    <template #content>
      <section v-if="!showMailForm">
        <ComponentPatientReleaseConfidentiality :patient="patient" />

        <div class="mt-4 border-t-1 pt-4">
          <ul class="space-y-3">
            <li>
              <label for="type-doc" class="flex items-center space-x-2">
                <input id="type-doc" v-model="type" type="radio" class="text-mcred" value="doc" />
                <div>Editierbare Vorlage für E-Mail an Arzt</div>
              </label>
            </li>

            <li>
              <label for="type-patient" class="flex items-center space-x-2">
                <input id="type-patient" v-model="type" type="radio" class="text-mcred" value="patient" />
                <div>Editierbare Vorlage für E-Mail an Patienten</div>
              </label>
            </li>
          </ul>
        </div>
      </section>

      <section v-else class="grid grid-cols-1 gap-8 sm:grid-cols-3">
        <ComponentAutocomplete
          ref="refRecipientAutocomplete"
          v-model="form.recipient_mail"
          :validation="form.errors.recipient_mail"
          :validation-on-empty-field="true"
          label="Empfänger E-Mail *"
          :fetch-method="autocompleteRecipient"
          key-name="email"
          clearable
          :min-search-term-length="2"
          :test-id="getTestId('send-mail-dialog-recipient-email')"
          @selected="selectRecipient"
          @cleared="form.recipient_mail = null"
        >
          <template #autocomplete-item="{item}">
            <div class="min-h-10 min-w-56">
              <div class="text-sm">{{ item.firstname }} {{ item.lastname }} {{ item.company }}</div>
              <div class="text-sm font-semibold">{{ item.email }}</div>
            </div>
          </template>

          <template #autocomplete-recommendation>
            <div
              v-if="
                (type === 'patient' && recommendedPatientEmailAddress) ||
                (type === 'doc' && recommendedRecipients.length > 0)
              "
              class="min-w-56 p-3"
            >
              <span v-if="type === 'patient'" class="text-gray-500">E-Mail aus Patientenanschrift:</span>
              <span v-else-if="type === 'doc'" class="text-gray-500">Verknüpfte Kontakte:</span>

              <ul class="-mx-2 space-y-2">
                <template v-if="type === 'patient'">
                  <li>
                    <button
                      type="button"
                      class="w-full rounded-md px-2 py-0.5 text-left text-gray-600 hover:bg-gray-100"
                      :data-test="getTestId('recipient-search-recommend-patient')"
                      @click="form.recipient_mail = recommendedPatientEmailAddress"
                    >
                      <div class="text-sm">{{ patient.firstname }} {{ patient.lastname }}</div>
                      <div class="text-sm font-semibold">{{ recommendedPatientEmailAddress }}</div>
                    </button>
                  </li>
                </template>
                <template v-else-if="type === 'doc'">
                  <li v-for="(recommendation, index) in recommendedRecipients" :key="index">
                    <button
                      type="button"
                      class="w-full rounded-md px-2 py-0.5 text-left text-gray-600 hover:bg-gray-100"
                      :data-test="getTestId(`recipient-search-recommend-${index}`)"
                      @click="selectRecipient(recommendation)"
                    >
                      <div class="text-sm">
                        {{ recommendation.firstname }} {{ recommendation.lastname }} {{ recommendation.company }}
                      </div>
                      <div class="text-sm font-semibold">{{ recommendation.email }}</div>
                    </button>
                  </li>
                </template>
              </ul>
            </div>
          </template>

          <template #autocomplete-empty>
            <div class="flex h-12 w-96 items-stretch">
              <div class="w-full self-center text-center">Keine Kontakte mit E-Mail-Adresse gefunden!</div>
            </div>
          </template>
        </ComponentAutocomplete>

        <ComponentInput
          v-model="form.sender_mail"
          :validation="form.errors.sender_mail"
          label="Absender E-Mail *"
          type="email"
          :test-id="getTestId('send-mail-dialog-sender-email')"
        />

        <MedFormField orientation="horizontal">
          <MedCheckbox
            v-model="form.carbon_copy"
            variant="primary"
            :data-test="getTestId('send-mail-dialog-carbon-copy-checkbox')"
          />

          <MedLabel>Kopie an Absender</MedLabel>
        </MedFormField>

        <ComponentInput
          v-model="form.subject"
          :validation="form.errors.subject"
          :privacy-guard="privacyErrors.subject"
          label="Betreff *"
          class="sm:col-span-3"
          :test-id="getTestId('send-mail-dialog-subject')"
        >
          <template v-if="type === 'doc'" #privacy-guard>
            <ComponentPrivacyGuard type="strict" :validation="privacyErrors.subject" />
          </template>
        </ComponentInput>

        <ComponentInput
          v-model="form.password"
          :validation="form.errors.password"
          label="PDF-Passwort (erstellen) *"
          type="password"
          :test-id="getTestId('send-mail-dialog-password')"
        />

        <ComponentInput
          v-model="form.password_confirmation"
          :validation="form.errors.password_confirmation"
          label="PDF-Passwort Wdh. *"
          type="password"
          :test-id="getTestId('send-mail-dialog-password-confirmation')"
        />

        <ComponentEditor
          :ref="`email-dialog-${type}`"
          class="col-start-1 sm:col-span-3"
          label="Text der E-Mail *"
          editor-class="min-h-48!"
          :privacy-guard="privacyErrors.body"
          :test-id="getTestId(`send-mail-dialog-${type}`)"
          :validation="form.errors.body"
          @initialized="(e) => handleMailBodyUpdate(e)"
          @input="(e) => handleMailBodyUpdate(e)"
          @update:model-value="(e) => handleMailBodyUpdate(e)"
        >
          <p>
            {{ recipientName }},
            <br />
            <br />
          </p>

          <p v-if="type === 'patient'">
            Sie haben uns in der Apotheke um eine Medikationsanalyse gebeten. Eine Zusammenfassung unserer Empfehlungen
            inkl. möglicher Lösungsvorschläge haben wir im Anhang passwortgeschützt beigefügt.
            <br />
            Das Passwort zum Öffnen des PDF-Anhangs teilen wir Ihnen auf folgende Weise mit …
            <br />
            <br />
          </p>

          <p v-else>
            {{ patientName }} hat uns in der Apotheke um eine Medikationsanalyse gebeten, wobei Probleme / Symptome in
            Verbindung mit der Arzneimitteltherapie vermutet wurden.
            <br />
            Eine Zusammenfassung inkl. möglicher Lösungsvorschläge haben wir im Anhang passwortgeschützt beigefügt.
            <br />
            Eine unterschriebene Datenschutzerklärung sowie eine unterschriebene Schweigepflichtentbindung für den
            Dialog mit Ihnen liegen vor.
            <br />
            <br />
            Sie können leicht direkt auf elektronischem Weg antworten: Klicken Sie im PDF-Dokument auf den jeweiligen
            Button „Rückmeldung geben" und übermitteln Sie dadurch Ihre Rückmeldungen digital an uns zurück.
            <br />
            <br />
            Wir bitten um zeitnahe Rückmeldung - Danke.
            <br />
            <br />
            Das Passwort zum Öffnen des PDF-Anhangs teilen wir Ihnen auf folgende Weise mit ...
            <br />
            <br />
          </p>

          <p>
            Mit besten Grüßen
            <br />
            <br />
            {{ senderName }}
            <br />
            <br />
            {{ senderCompany }}
            <br />
            {{ senderCity }}
          </p>

          <template v-if="type === 'doc'" #privacy-guard>
            <ComponentPrivacyGuard type="strict" :validation="privacyErrors.body" />
          </template>
        </ComponentEditor>

        <div class="text-right sm:col-span-3">* Pflichtangaben</div>
      </section>
    </template>

    <template #actions>
      <template v-if="!showMailForm">
        <!-- TODO Should it be disabled when nothing is selected? -->
        <MedButton variant="primary" :data-test="getTestId('send-mail-dialog-open-button')" @click="write()">
          Öffnen
        </MedButton>

        <MedButton :data-test="getTestId('send-mail-dialog-cancel-button')" @click="close()">Abbrechen</MedButton>
      </template>

      <template v-else>
        <MedButton
          variant="primary"
          :disabled="
            form.processing || pendingAjaxRequests > 0 || Object.values(privacyErrors).some((value) => value !== null)
          "
          :data-test="getTestId('send-mail-dialog-create-pdf-button')"
          @click="send()"
        >
          PDF erstellen und senden
        </MedButton>

        <MedButton
          :disabled="pendingAjaxRequests > 0"
          :data-test="getTestId('send-mail-dialog-preview-pdf-button')"
          @click="preview()"
        >
          PDF Vorschau
        </MedButton>

        <MedButton flat variant="primary" :data-test="getTestId('send-mail-dialog-cancel-button')" @click="close()">
          Abbrechen
        </MedButton>
      </template>
    </template>

    <template v-if="showMailForm" #info-icon>
      <MedInfoButton
        size="lg"
        side="left"
        aria-label="E-Mail Hinweise"
        disable-portal
        :data-test="getTestId('send-mail-dialog-info-button')"
      >
        <p class="mb-2 font-bold">Hinweise:</p>
        <ul class="list-disc space-y-1 pl-4 text-sm">
          <li>
            Nur die „an {{ type === "patient" ? "Patient" : "Arzt" }}" markierten Ergebnisse werden in den PDF-Anhang
            der Mail übermittelt.
          </li>
          <li>
            Vergeben Sie hier (unten) selbst ein Passwort zur Verschlüsselung der PDF-Datei, das dem Empfänger bekannt
            ist.
          </li>
          <li>Teilen Sie das Passwort auf einem anderen Weg mit (Telefon, persönlich, etc.).</li>
          <li>
            Datenschutz:
            <MedLink
              variant="primary"
              :href="$route('patients.edit', {patient: patient.id}) + '#patient-documents'"
              target="_blank"
              :data-test="getTestId('send-mail-dialog-patient-consent-link')"
            >
              Einwilligung des Patienten
            </MedLink>
            sollte vorliegen, handschriftlich oder digital.
          </li>
          <li>Die Beachtung datenschutzrechtlicher Bestimmungen obliegt der Apotheke / dem Apotheker.</li>
        </ul>
      </MedInfoButton>
    </template>
  </ComponentDialog>
</template>
